import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useSessionLossRedirect from "./useSessionLossRedirect";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import {
  AccessTime,
  AssignmentTurnedIn,
  BarChart,
  Cancel,
  CheckCircle,
  Comment,
  Info,
  Warning,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ClosejobOperationbutton({
  startedTime,
  jobNumber,
  product,
  username,
  customer,
  operation,
}) {
  const navigate = useNavigate();
  const handleSessionLoss = useSessionLossRedirect();

  // UI state
  const [firstPopupOpen, setFirstPopupOpen] = useState(false);
  const [secondPopupOpen, setSecondPopupOpen] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Snackbar + errors
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [error, setError] = useState("");

  // Machine + FG config
  const [machineCode, setMachineCode] = useState("");
  const [finishedGood, setFinishedGood] = useState(""); // "yes" | "no"
  const [isRadioDisabled, setIsRadioDisabled] = useState(false);

  // Base quantities
  const [productionQuantity, setProductionQuantity] = useState("");
  const [wasteQuantity, setWasteQuantity] = useState("");
  const [remarks, setRemarks] = useState("");

  // QCM (quality) fields
  const [selectedPass, setSelectedPass] = useState("");
  const [inputQuantity, setInputQuantity] = useState("");
  const [passQuantity, setPassQuantity] = useState("");
  const [rejectQuantity, setRejectQuantity] = useState("");

  const CHARACTER_LIMIT = 120;

  // Format started time (Asia/Colombo +5:30 offset)
  const formattedStartedTime = (() => {
    if (!startedTime) return "";
    const d = new Date(startedTime);
    d.setHours(d.getHours() + 5);
    d.setMinutes(d.getMinutes() + 30);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  })();

  useEffect(() => {
    let redirectTimer;

    // Helper: schedule redirect to login after showing the error briefly.
    const scheduleRedirectToLogin = (msg) => {
      setError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: "error",
      });
      redirectTimer = setTimeout(() => {
        navigate("/", { replace: true });
      }, 2500);
    };

    // Load machine config and FG flag on mount
    const loadConfig = async () => {
      try {
        setError("");
        // get machine code for username
        const mcRes = await axios.get(
          `${API_BASE_URL}/getMachineCode?username=${username}`
        );
        const mc = mcRes?.data?.machineCode || "";

        // If we got no machineCode, the session was likely cleared by an
        // admin (or never set up properly). Backend doesn't use 401/403
        // for session issues, so we treat "no machineCode" as the signal.
        if (!mc) {
          scheduleRedirectToLogin(
            "Session cleared. Redirecting to login..."
          );
          return;
        }

        setMachineCode(mc);

        // check FG capability
        const fgRes = await axios.get(`${API_BASE_URL}/Isfinishedgood/${mc}`);
        const finishedGoodFlag = fgRes?.data?.FinishedGood; // 0/1
        if (finishedGoodFlag === 0) {
          setFinishedGood("no");
          setIsRadioDisabled(true);
        }

        setSnackbar({
          open: true,
          message: "Machine configuration loaded.",
          severity: "success",
        });
      } catch (e) {
        console.error(e);

        // Session-loss detection. Three signals trigger a redirect:
        //   1. Explicit auth status (401/403)
        //   2. Backend "no session"-style error messages (matches the
        //      pattern LoginForm uses in isNoActiveSessionError)
        //   3. 404 on getMachineCode (user/session record gone)
        // Pure transient failures (network errors, 500s without those
        // signals) keep the original "show error, do not redirect"
        // behavior so in-progress form data is preserved.
        const status = e?.response?.status;
        const message = (
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          ""
        ).toLowerCase();

        const isAuthError = status === 401 || status === 403;
        const isNotFound = status === 404;
        const isNoSessionMessage =
          message.includes("no active") ||
          message.includes("no ongoing") ||
          message.includes("not found") ||
          message.includes("no session") ||
          message.includes("session not found");

        if (isAuthError || isNotFound || isNoSessionMessage) {
          scheduleRedirectToLogin(
            "Session expired. Redirecting to login..."
          );
        } else {
          setError("Failed to load machine configuration.");
          setSnackbar({
            open: true,
            message: "Machine configuration error.",
            severity: "error",
          });
        }
      }
    };

    if (username) loadConfig();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [username, navigate]);

  const resetFields = () => {
    setProductionQuantity("");
    setWasteQuantity("");
    setRemarks("");
    setFinishedGood("");
    setSelectedPass("");
    setInputQuantity("");
    setPassQuantity("");
    setRejectQuantity("");
    setError("");
  };

  const handleOpenFirstPopup = () => setFirstPopupOpen(true);
  const handleCloseFirstPopup = () => setFirstPopupOpen(false);

  const handleOpenSecondPopup = () => {
    setFirstPopupOpen(false);
    setFormVisible(true);
    setSecondPopupOpen(true);
  };

  const handleSnackbarClose = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  const onlyNumber = (v) => (/^\d*\.?\d*$/.test(v) ? v : "");
  const onNumChange = (setter) => (e) => setter(onlyNumber(e.target.value));

  const isQcm = machineCode?.startsWith?.("QCM");
  const isFormValid = () => {
    if (!finishedGood) return false;
    if (!productionQuantity.trim() || !wasteQuantity.trim()) return false;
    if (!isQcm) return true;

    // QCM extra required
    return (
      !!selectedPass &&
      !!inputQuantity.trim() &&
      !!passQuantity.trim() &&
      !!rejectQuantity.trim()
    );
  };

  const computePercent = (numStr, denStr) => {
    const n = parseFloat(numStr || "0");
    const d = parseFloat(denStr || "0");
    if (d <= 0) return "";
    return ` (${((n / d) * 100).toFixed(2)}%)`;
  };

  const buildRemarks = () => {
    const prodPct = computePercent(productionQuantity, inputQuantity);
    const wastePct = computePercent(wasteQuantity, inputQuantity);
    const fgCode = finishedGood === "yes" ? "[FG]" : "";

    let base = `Production_Qty: ${productionQuantity}${prodPct}${fgCode}, ` +
               `Waste_Qty: ${wasteQuantity}${wastePct}`;
    const clean = remarks.replace(/\s+/g, " ").trim();
    if (clean) base += `, Remarks: ${clean}`;

    if (isQcm) {
      const passPct = computePercent(passQuantity, inputQuantity);
      const rejPct = computePercent(rejectQuantity, inputQuantity);
      const qcm =
        `Selected Pass: ${selectedPass}, ` +
        `Input Qty: ${inputQuantity}, ` +
        `Pass Qty: ${passQuantity}${passPct}, ` +
        `Rejected Qty: ${rejectQuantity}${rejPct}, `;
      return `{${qcm}${base}}`;
    }
    return base;
  };

  const submitNextPassQcm = async () => {
    // QCM “save & next pass”
    setIsLoading(true);
    setError("");
    try {
      if (!finishedGood) {
        setError("Please select Finished Good option.");
        return;
      }

      const endedTime = new Date().toISOString(); // ISO for APIs
      const payload = {
        job_number: jobNumber,
        customer,
        product,
        main_operation: operation,
        username,
        machineCode,
        start_datetime: startedTime,
        endedTime,
        JobStartedTime: startedTime,
        productionQty: productionQuantity,
        wasteQty: wasteQuantity,
        finishedGood,
        status: "Finished",
        remarks: buildRemarks(),
        jobCloseremarks: remarks.replace(/\s+/g, " ").trim(),
        operation_type: "QC_Pass",
        operation_description: `QC Pass-${selectedPass}`,
        inputQuantity: inputQuantity || 0,
        passQuantity: passQuantity || 0,
      };

      const res = await axios.post(`${API_BASE_URL}/QcpassUpdate`, payload);
      if (res.status === 200) {
        resetFields();
        setSecondPopupOpen(false);
        setSnackbar({
          open: true,
          message: "QC Pass saved. You can proceed to next pass.",
          severity: "success",
        });
      } else {
        setError("Failed to save QC pass.");
      }
    } catch (e) {
      console.error(e);
      if (handleSessionLoss(e)) {
        setError("Session expired. Redirecting to login...");
        return;
      }
      setError("Error while saving QC pass.");
    } finally {
      setIsLoading(false);
    }
  };

  const completeOperation = async () => {
    setIsLoading(true);
    setError("");
    try {
      if (!finishedGood) {
        setError("Please select Finished Good option.");
        return;
      }

      const endedTime = new Date().toISOString();
      const payload = {
        jobNumber,
        customer,
        operation,
        product,
        username,
        machineCode,
        startedTime,
        endedTime,
        remarks: buildRemarks(),
        productionQuantity,
        jobCloseremarks: remarks.replace(/\s+/g, " ").trim(),
        finishedGood,
        wasteQuantity,
        inputQuantity: inputQuantity || 0,
        passQuantity: passQuantity || 0,
      };

      const res = await axios.post(
        `${API_BASE_URL}/CloseJobOperation`,
        payload
      );
      if (res.status === 200) {
        resetFields();
        setSecondPopupOpen(false);
        setSnackbar({
          open: true,
          message: "Job operation completed!",
          severity: "success",
        });
        navigate("/JobProcessSelection", { state: { username } });
      } else {
        setError("Failed to close job operation.");
      }
    } catch (e) {
      console.error(e);
      if (handleSessionLoss(e)) {
        setError("Session expired. Redirecting to login...");
        return;
      }
      setError("Error while closing job operation.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: 180, p: 0 }}>
      {/* Trigger */}
      <Button
        variant="contained"
        size="small"
        onClick={handleOpenFirstPopup}
        disabled={isLoading}
        sx={{
          width: "100%",
          py: 0.5,
          fontWeight: 600,
          fontSize: "0.875rem",
          borderRadius: 2,
          textTransform: "none",
          color: "common.white",
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
          boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`,
          "&:hover:not(:disabled)": {
            transform: "translateY(-2px)",
            boxShadow: (t) => `0 8px 25px ${alpha(t.palette.primary.main, 0.5)}`,
          },
          "&:disabled": {
            background: (t) =>
              `linear-gradient(135deg, ${alpha(
                t.palette.text.disabled,
                0.3
              )}, ${alpha(t.palette.text.disabled, 0.5)})`,
            boxShadow: (t) => `0 3px 10px ${alpha(t.palette.text.disabled, 0.3)}`,
          },
        }}
      >
        Close Job Operation
      </Button>

      {/* FIRST DIALOG — confirmation */}
      <Dialog
        open={firstPopupOpen}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: (t) => ({
            borderRadius: 2,
            backgroundColor: alpha(t.palette.background.paper, 0.98),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
            boxShadow: 12,
            minWidth: 400,
            maxWidth: 450,
            overflow: "hidden",
          }),
        }}
      >
        <DialogTitle
          sx={(t) => ({
            textAlign: "center",
            pb: 1,
            pt: 2,
            borderBottom: `1px solid ${t.palette.divider}`,
            background: alpha(t.palette.primary.main, 0.04),
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Warning sx={{ fontSize: 28, color: "warning.main" }} />
            <Typography
              variant="h6"
              fontWeight={600}
              sx={(t) => ({
                background: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              })}
            >
              Confirm Job Closure
            </Typography>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}
          >
            <Chip
              label={`Job: ${jobNumber}`}
              size="small"
              sx={(t) => ({
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: alpha(t.palette.primary.main, 0.12),
                color: t.palette.primary.main,
                border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
              })}
            />
            <Chip
              label={product}
              size="small"
              sx={(t) => ({
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: alpha(t.palette.secondary.main, 0.12),
                color: t.palette.secondary.main,
                border: `1px solid ${alpha(t.palette.secondary.main, 0.25)}`,
              })}
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", py: 2, px: 2.5 }}>
          <Paper
            elevation={0}
            sx={(t) => ({
              p: 1.5,
              mb: 2,
              backgroundColor: alpha(t.palette.primary.main, 0.06),
              borderRadius: 2,
              border: `1px solid ${alpha(t.palette.primary.main, 0.15)}`,
            })}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
            >
              <AccessTime sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={500} color="text.primary">
                Started: {formattedStartedTime}
              </Typography>
            </Box>
          </Paper>

          <Typography variant="subtitle1" fontWeight={500} color="text.primary" sx={{ mb: 0.5 }}>
            Are you sure you want to close this job operation?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will finalize all production data and cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={(t) => ({
            justifyContent: "center",
            gap: 2,
            pb: 2.5,
            px: 2.5,
            borderTop: `1px solid ${t.palette.divider}`,
          })}
        >
          <Button
            onClick={handleOpenSecondPopup}
            variant="contained"
            startIcon={<CheckCircle />}
            disabled={isLoading}
            sx={(t) => ({
              px: 3,
              py: 1,
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: "0.9rem",
              textTransform: "none",
              color: "common.white",
              background: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
            })}
          >
            Yes, Continue
          </Button>
          <Button
            onClick={handleCloseFirstPopup}
            variant="outlined"
            startIcon={<Cancel />}
            disabled={isLoading}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: "0.9rem",
              textTransform: "none",
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": { borderColor: "primary.dark" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* SECOND DIALOG — form */}
      <Dialog
        open={secondPopupOpen}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        onClose={() => {
          setSecondPopupOpen(false);
          setFormVisible(false);
        }}
        PaperProps={{
          sx: (t) => ({
            borderRadius: 2,
            backgroundColor: alpha(t.palette.background.paper, 0.98),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
            boxShadow: 12,
            maxHeight: "85vh",
            height: "auto",
            minHeight: 500,
            "@media (max-height: 650px)": { maxHeight: "95vh" },
            "@media (max-width: 1100px)": { maxHeight: "90vh", m: 1 },
            overflow: "hidden",
          }),
        }}
      >
        {isLoading && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              borderRadius: "16px 16px 0 0",
            }}
          />
        )}

        {/* Header */}
        <DialogTitle
          sx={(t) => ({
            borderBottom: `1px solid ${t.palette.divider}`,
            background: alpha(t.palette.primary.main, 0.04),
            pt: 1.5,
            pb: 1.5,
          })}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <AssignmentTurnedIn sx={{ fontSize: 28, color: "primary.main" }} />
              <Typography
                variant="h5"
                fontWeight={600}
                sx={(t) => ({
                  background: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                })}
              >
                Complete Job Operation
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Chip
                label={`Job: ${jobNumber}`}
                size="small"
                sx={(t) => ({
                  backgroundColor: alpha(t.palette.primary.main, 0.12),
                  color: t.palette.primary.main,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                })}
              />
              <Chip
                label={`Product: ${product}`}
                size="small"
                sx={(t) => ({
                  backgroundColor: alpha(t.palette.secondary.main, 0.12),
                  color: t.palette.secondary.main,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                })}
              />
              <Chip
                label={`Customer: ${customer}`}
                size="small"
                sx={(t) => ({
                  backgroundColor: alpha(t.palette.primary.main, 0.12),
                  color: t.palette.primary.main,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                })}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <AccessTime sx={{ fontSize: 14 }} />
              Started: {formattedStartedTime}
            </Typography>
          </Box>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            p: 2,
            backgroundColor: (t) => alpha(t.palette.background.default, 0.02),
            overflow: "auto",
            maxHeight: "calc(85vh - 160px)",
            "@media (max-height: 650px)": {
              maxHeight: "calc(95vh - 160px)",
            },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: "0.875rem" }}>
              {error}
            </Alert>
          )}

          <Fade in={formVisible} timeout={400}>
            <Box>
              {/* Finished Good */}
              <Paper
                elevation={0}
                sx={(t) => ({
                  p: 2,
                  mb: 2,
                  backgroundColor: alpha(t.palette.primary.main, 0.04),
                  borderRadius: 2,
                  border: `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
                })}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.main",
                    fontWeight: 600,
                  }}
                >
                  <Info sx={{ fontSize: 20 }} />
                  Finished Good?
                </Typography>

                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={finishedGood}
                    onChange={(e) => setFinishedGood(e.target.value)}
                    sx={{ justifyContent: "center" }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={
                        <Radio
                          size="small"
                          disabled={isRadioDisabled}
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                      }
                      label="yes"
                      sx={{ mr: 3, color: "text.secondary", fontWeight: 500 }}
                    />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          size="small"
                          disabled={isRadioDisabled}
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" },
                          }}
                        />
                      }
                      label="no"
                      sx={{ color: "text.secondary", fontWeight: 500 }}
                    />
                  </RadioGroup>
                </FormControl>
              </Paper>

              {/* QCM details */}
              {finishedGood && isQcm && (
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    p: 2,
                    mb: 2,
                    backgroundColor: alpha(t.palette.secondary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${alpha(t.palette.secondary.main, 0.12)}`,
                  })}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "secondary.main",
                      fontWeight: 600,
                    }}
                  >
                    <BarChart sx={{ fontSize: 20 }} />
                    Quality Control Details
                  </Typography>

                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Pass</InputLabel>
                        <Select
                          value={selectedPass}
                          label="Select Pass"
                          onChange={(e) => setSelectedPass(e.target.value)}
                          disabled={isLoading}
                          sx={(t) => ({
                            borderRadius: 2,
                            backgroundColor: alpha(t.palette.background.paper, 0.98),
                          })}
                        >
                          <MenuItem value="">
                            <em>Select Pass</em>
                          </MenuItem>
                          <MenuItem value="1">Pass 01</MenuItem>
                          <MenuItem value="2">Pass 02</MenuItem>
                          <MenuItem value="3">Pass 03</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {[
                      { key: "inputQty", label: "Input Qty", value: inputQuantity, setter: setInputQuantity },
                      { key: "passQty", label: "Pass Qty", value: passQuantity, setter: setPassQuantity },
                      { key: "rejectQty", label: "Reject Qty", value: rejectQuantity, setter: setRejectQuantity },
                    ].map((f) => (
                      <Grid item xs={12} sm={3} key={f.key}>
                        <TextField
                          label={f.label}
                          fullWidth
                          size="small"
                          value={f.value}
                          onChange={onNumChange(f.setter)}
                          inputProps={{ maxLength: 8 }}
                          disabled={isLoading}
                          sx={(t) => ({
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              backgroundColor: alpha(t.palette.background.paper, 0.98),
                            },
                          })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* Production summary */}
              {finishedGood && (
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    p: 2,
                    mb: 2,
                    backgroundColor: alpha(t.palette.primary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
                  })}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    <BarChart sx={{ fontSize: 20 }} />
                    Production Summary
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Production Quantity"
                        fullWidth
                        size="small"
                        value={productionQuantity}
                        onChange={onNumChange(setProductionQuantity)}
                        inputProps={{ maxLength: 8 }}
                        error={!productionQuantity.trim()}
                        disabled={isLoading}
                        sx={(t) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: alpha(t.palette.background.paper, 0.98),
                          },
                        })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Waste Quantity"
                        fullWidth
                        size="small"
                        value={wasteQuantity}
                        onChange={onNumChange(setWasteQuantity)}
                        inputProps={{ maxLength: 8 }}
                        error={!wasteQuantity.trim()}
                        disabled={isLoading}
                        sx={(t) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: alpha(t.palette.background.paper, 0.98),
                          },
                        })}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Remarks */}
              {finishedGood && (
                <Paper
                  elevation={0}
                  sx={(t) => ({
                    p: 2,
                    backgroundColor: alpha(t.palette.secondary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${alpha(t.palette.secondary.main, 0.12)}`,
                  })}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "secondary.main",
                      fontWeight: 600,
                    }}
                  >
                    <Comment sx={{ fontSize: 20 }} />
                    Remarks (Optional)
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={remarks}
                    onChange={(e) => {
                      if (e.target.value.length <= CHARACTER_LIMIT) setRemarks(e.target.value);
                    }}
                    inputProps={{ maxLength: CHARACTER_LIMIT }}
                    helperText={`${remarks.length}/${CHARACTER_LIMIT} characters`}
                    disabled={isLoading}
                    placeholder="Enter any additional comments..."
                    sx={(t) => ({
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: alpha(t.palette.background.paper, 0.98),
                      },
                    })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Comment sx={{ color: "action.active" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Paper>
              )}
            </Box>
          </Fade>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={(t) => ({
            p: 2,
            gap: 2,
            borderTop: `1px solid ${t.palette.divider}`,
            backgroundColor: alpha(t.palette.background.default, 0.02),
            justifyContent: "space-between",
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              User: <strong>{username}</strong>
            </Typography>
            {isFormValid() && (
              <Chip
                icon={<CheckCircle />}
                label="Ready"
                color="success"
                size="small"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            {isQcm && (
              <Button
                variant="contained"
                onClick={submitNextPassQcm}
                disabled={!isFormValid() || isLoading}
                sx={(t) => ({
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  color: "common.white",
                  background: isFormValid()
                    ? `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`
                    : `linear-gradient(135deg, ${alpha(t.palette.text.disabled, 0.3)}, ${alpha(
                        t.palette.text.disabled,
                        0.5
                      )})`,
                })}
              >
                Save & Next Pass
              </Button>
            )}
            <Button
              variant="contained"
              onClick={completeOperation}
              disabled={!isFormValid() || isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <CheckCircle />
                )
              }
              sx={(t) => ({
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                minWidth: 160,
                color: "common.white",
                background: isFormValid()
                  ? `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`
                  : `linear-gradient(135deg, ${alpha(t.palette.text.disabled, 0.3)}, ${alpha(
                      t.palette.text.disabled,
                      0.5
                    )})`,
              })}
            >
              {isLoading ? "Processing..." : "Complete Operation"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleSnackbarClose}
          sx={{ borderRadius: 1, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

ClosejobOperationbutton.propTypes = {
  startedTime: PropTypes.string.isRequired,
  jobNumber: PropTypes.string.isRequired,
  product: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  customer: PropTypes.string.isRequired,
  operation: PropTypes.string.isRequired,
};